// [NEW FILE] Requirement 1: the user can resize every table column.
//
// Works on every <table class="data-table"> in the PMS (DataTable and the hand-written tables), because it is
// called by the table watcher in utils/autoTableColumns.js (started once in layouts/AppLayout.jsx).
//
// How it works for the user:
// - Each column header has a thin handle on its LEFT and on its RIGHT edge.
//   Right edge: drag right / left to make the column wider / narrower (the table grows or shrinks).
//   Left edge: moves the border with the column before it (one gets wider, the other narrower; first column: only itself).
// - With the keyboard: focus the handle (Tab) and press Arrow Left / Arrow Right (Shift = bigger steps).
// - Double-click a handle to go back to the automatic widths for that table.
// - Only the table you resize changes: widths are stored per table (page + position of the table on the page + its
//   column titles), so other tables, even with the same columns, keep their own widths.
//
// Technically: on the first drag every header gets its current pixel width and the table switches to
// `table-layout: fixed` (class .is-resized), so moving one column never changes the others; the table becomes
// wider or narrower and its box scrolls sideways when needed.
const MIN_WIDTH = 48;
const STORE_PREFIX = "pms.colwidths:";

function headerCells(table) {
  const head = table.tHead;
  if (!head || !head.rows.length) return [];
  return Array.from(head.rows[head.rows.length - 1].cells);
}

// One key per table: page path + the table's position among the tables of the page + its header titles.
function storeKey(table) {
  const titles = headerCells(table).map((th) => (th.innerText || th.textContent || "").trim().slice(0, 30)).join("|");
  const path = typeof window !== "undefined" ? window.location.pathname.replace(/\/\d+(?=\/|$)/g, "/:id") : "";
  const index = typeof document !== "undefined" ? Array.from(document.querySelectorAll("table.data-table")).indexOf(table) : 0;
  return `${STORE_PREFIX}${path}#${index}#${titles}`;
}

const keyOf = (table) => table.dataset.colKey || storeKey(table);

function readWidths(key) {
  try { return JSON.parse(window.localStorage.getItem(key) || "null"); } catch { return null; }
}
function writeWidths(key, widths) {
  try {
    if (widths) window.localStorage.setItem(key, JSON.stringify(widths));
    else window.localStorage.removeItem(key);
  } catch { /* storage blocked: widths are kept for this visit only */ }
}

function applyWidths(table, widths) {
  const cells = headerCells(table);
  if (!widths || widths.length !== cells.length) return false;
  cells.forEach((th, i) => { th.style.width = `${widths[i]}px`; });
  table.style.width = `${widths.reduce((a, b) => a + b, 0)}px`;
  table.classList.add("is-resized");
  return true;
}

function freeze(table) {
  const cells = headerCells(table);
  const widths = cells.map((th) => Math.max(MIN_WIDTH, Math.round(th.getBoundingClientRect().width)));
  applyWidths(table, widths);
  return widths;
}

export function resetTable(table) {
  headerCells(table).forEach((th) => { th.style.width = ""; });
  table.style.width = "";
  table.classList.remove("is-resized");
  writeWidths(keyOf(table), null);
}

function currentWidths(table) {
  return headerCells(table).map((th) => Math.round(parseFloat(th.style.width) || th.getBoundingClientRect().width));
}

function resizeColumn(table, index, width) {
  const widths = table.classList.contains("is-resized") ? currentWidths(table) : freeze(table);
  widths[index] = Math.max(MIN_WIDTH, Math.round(width));
  applyWidths(table, widths);
  return widths;
}

// Left edge: move the border between column index-1 and column index by dx (total width unchanged).
function moveLeftEdge(table, index, startWidths, dx) {
  const widths = [...startWidths];
  if (index === 0) {
    widths[0] = Math.max(MIN_WIDTH, Math.round(startWidths[0] - dx));
  } else {
    const total = startWidths[index - 1] + startWidths[index];
    const prev = Math.min(total - MIN_WIDTH, Math.max(MIN_WIDTH, Math.round(startWidths[index - 1] + dx)));
    widths[index - 1] = prev;
    widths[index] = total - prev;
  }
  applyWidths(table, widths);
  return widths;
}

// Adds the drag handles (once per header cell) and restores remembered widths. Safe to call again and again.
export function makeResizable(table) {
  const cells = headerCells(table);
  if (!cells.length || table.dataset.noResize !== undefined) return;
  cells.forEach((th, index) => {
    if (th.querySelector(":scope > .col-resizer")) return;
    if (th.classList.contains("col-select")) return; // the checkbox column keeps its size
    const title = (th.innerText || th.textContent || "").trim() || `column ${index + 1}`;
    ["left", "right"].forEach((side) => {
      const handle = document.createElement("span");
      handle.className = `col-resizer is-${side}`;
      handle.dataset.side = side;
      handle.setAttribute("role", "separator");
      handle.setAttribute("aria-orientation", "vertical");
      handle.setAttribute("tabindex", "0");
      handle.setAttribute("aria-label", `Resize ${title} column from its ${side} edge (drag, or Arrow keys; double-click to reset)`);
      handle.title = "Drag to resize. Double-click to reset.";
      th.appendChild(handle);
    });
  });
  const key = storeKey(table);
  if (table.dataset.colKey !== key) {
    table.dataset.colKey = key;
    const saved = readWidths(key);
    if (saved) applyWidths(table, saved);
  }
}

let installed = false;

// One set of document listeners handles every handle on every page.
export function installColumnResizing() {
  if (installed || typeof document === "undefined") return;
  installed = true;

  document.addEventListener("pointerdown", (e) => {
    const handle = e.target.closest?.(".col-resizer");
    if (!handle) return;
    const th = handle.parentElement;
    const table = th?.closest("table");
    if (!table) return;
    e.preventDefault();
    e.stopPropagation();
    const index = headerCells(table).indexOf(th);
    const startX = e.clientX;
    const startWidth = th.getBoundingClientRect().width;
    if (!table.classList.contains("is-resized")) freeze(table);
    document.body.classList.add("is-col-resizing");
    handle.classList.add("is-active");
    let widths = currentWidths(table);
    const startWidths = [...widths];
    const left = handle.dataset.side === "left";
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      widths = left ? moveLeftEdge(table, index, startWidths, dx) : resizeColumn(table, index, startWidth + dx);
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
      document.body.classList.remove("is-col-resizing");
      handle.classList.remove("is-active");
      writeWidths(keyOf(table), widths);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
  }, true);

  // A click on a handle must not sort / open anything behind it.
  document.addEventListener("click", (e) => {
    if (e.target.closest?.(".col-resizer")) { e.preventDefault(); e.stopPropagation(); }
  }, true);

  document.addEventListener("dblclick", (e) => {
    const handle = e.target.closest?.(".col-resizer");
    if (!handle) return;
    e.preventDefault();
    const table = handle.closest("table");
    if (table) resetTable(table);
  }, true);

  document.addEventListener("keydown", (e) => {
    const handle = e.target.closest?.(".col-resizer");
    if (!handle || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
    e.preventDefault();
    const th = handle.parentElement;
    const table = th.closest("table");
    const index = headerCells(table).indexOf(th);
    const step = (e.shiftKey ? 50 : 10) * (e.key === "ArrowLeft" ? -1 : 1);
    if (!table.classList.contains("is-resized")) freeze(table);
    const widths = handle.dataset.side === "left"
      ? moveLeftEdge(table, index, currentWidths(table), step)
      : resizeColumn(table, index, th.getBoundingClientRect().width + step);
    writeWidths(keyOf(table), widths);
  }, true);
}
